import { v4 as uuidv4 } from 'uuid';
import { getValueFromSession, setValueInSession } from './storage';
import { getCachedFeatureFlag } from './featureFlags';

/**
 * Manages the per-session correlation ID used to tie this app's telemetry
 * (utills/telemetry.ts) and blob request headers together, and to line up
 * with the linking mobile app's own logs for the same session. Exposes
 * getCorrelationId (resolve/generate), setCorrelationId (override once the
 * mobile app's ID is known), and getCorrelationIdHeader (the header to send,
 * gated by the enableTelemetry feature flag).
 */

const CORRELATION_ID_KEY = 'correlationId';

// uuid's v4() prefers crypto.getRandomValues/randomUUID when available and
// falls back to Math.random() otherwise, matching the previous hand-rolled
// generateId() behaviour without maintaining that logic ourselves.
const generateId = (): string => uuidv4();

const getCorrelationIdFromUrl = (): string | null =>
  new URLSearchParams(window.location.search).get(CORRELATION_ID_KEY);

let cachedCorrelationId: string | null = null;

/**
 * Resolves this session's correlation ID, generating and caching one if
 * needed. One correlation ID per app session, reused across page
 * navigations and persisted the same way as the rest of the app's session
 * state (storage.ts) so it survives an accidental reload mid-flow rather
 * than resetting.
 *
 * The linking mobile app passes its own correlation ID as a `correlationId`
 * query param so its logs can be cross-referenced with the ones this app
 * sends to App Insights and Azure Storage; that value takes priority over
 * anything already stored. Falls back to a locally generated ID when the
 * app is opened without that param.
 * @returns the resolved correlation ID
 */
export const getCorrelationId = (): string => {
  if (cachedCorrelationId) {
    return cachedCorrelationId;
  }

  const fromUrl = getCorrelationIdFromUrl();
  if (fromUrl) {
    cachedCorrelationId = fromUrl;
    setValueInSession(CORRELATION_ID_KEY, fromUrl);
    return fromUrl;
  }

  const existing = getValueFromSession(CORRELATION_ID_KEY);
  if (existing && typeof existing === 'string') {
    cachedCorrelationId = existing;
    return existing;
  }

  const id = generateId();
  setValueInSession(CORRELATION_ID_KEY, id);
  cachedCorrelationId = id;
  return id;
};

/**
 * Overrides the resolved correlation ID once the mobile app's own ID is
 * known. The mobile app's correlation ID isn't always in the top-level
 * `correlationId` query param — it can also come back inside the decrypted
 * link payload (`dataDecrypt.correlationId` in routers/index.tsx), only
 * known once that decrypt API call resolves. Callers push it in here as
 * soon as they find it, overriding whatever getCorrelationId() had already
 * resolved (URL, storage, or generated) so headers/telemetry sent
 * afterwards use the right value. A falsy/non-string id is ignored.
 * @param id the correlation ID from the decrypted link payload
 */
export const setCorrelationId = (id: unknown): void => {
  if (!id || typeof id !== 'string') return;
  cachedCorrelationId = id;
  setValueInSession(CORRELATION_ID_KEY, id);
};

/**
 * The x-correlation-id header to attach to blob requests. Request headers
 * should stop carrying the correlation ID once the enableTelemetry flag
 * goes false, matching the telemetry.ts behaviour of no longer emitting
 * telemetry for this session, so this returns an empty object in that case.
 * @returns the header object to spread into a request's headers
 */
export const getCorrelationIdHeader = (): Record<string, string> =>
  getCachedFeatureFlag('enableTelemetry', true)
    ? { 'x-correlation-id': getCorrelationId() }
    : {};
